import { useCallback, useMemo, useRef, useState } from "react";
import { Plus } from "lucide-react";

import { Button } from "@/components/_atoms/button";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";

import type { CurrentFieldValues, DataRequestFieldResponse, SaveFieldHandler } from "../../models/data-request.model";

import { IndicatorValueRow } from "./indicator-value-row";
import { buildInitialRows, toEmittedRows, type ValueRow } from "./indicator-value-rows";

type IndicatorDataLine = NonNullable<CurrentFieldValues["dataLineList"]>[number];
type IndicatorValue = NonNullable<CurrentFieldValues["values"]>[number];

interface IndicatorValuesFieldProps {
  dataLineList: IndicatorDataLine[];
  currentValues: IndicatorValue[];
  savedResponse: DataRequestFieldResponse | null;
  onSave: SaveFieldHandler;
  disabled?: boolean;
}

export function IndicatorValuesField({
  dataLineList,
  currentValues,
  savedResponse,
  onSave,
  disabled,
}: IndicatorValuesFieldProps) {
  const { t } = useDomainTranslation("data-request");

  const defaultDataLineId = useMemo(
    () => dataLineList.find((line) => line.isDefault)?.id ?? dataLineList[0]?.id ?? "",
    [dataLineList],
  );

  const [rows, setRows] = useState<ValueRow[]>(() =>
    buildInitialRows(savedResponse, currentValues, defaultDataLineId),
  );
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const nextClientKey = useRef(0);

  const applyRows = useCallback(
    (nextRows: ValueRow[]) => {
      setRows(nextRows);
      onSave({ fieldKey: "values", newValue: { rows: toEmittedRows(nextRows) } });
    },
    [onSave],
  );

  const updateRow = useCallback(
    (clientKey: string, patch: Partial<ValueRow>) => {
      applyRows(rowsRef.current.map((row) => (row.clientKey === clientKey ? { ...row, ...patch } : row)));
    },
    [applyRows],
  );

  const removeRow = useCallback(
    (clientKey: string) => {
      applyRows(rowsRef.current.filter((row) => row.clientKey !== clientKey));
    },
    [applyRows],
  );

  const addRow = useCallback(() => {
    nextClientKey.current += 1;
    applyRows([
      ...rowsRef.current,
      { clientKey: `new-${nextClientKey.current}`, dataLineId: defaultDataLineId, date: "", value: "", comment: "" },
    ]);
  }, [applyRows, defaultDataLineId]);

  return (
    <div className="rounded-md border">
      <div className="border-b bg-muted/50 px-3 py-2 text-sm font-medium">{t("dataEntry.values.title")}</div>
      <div className="flex flex-col gap-2 p-3">
        {rows.length === 0 && <p className="text-sm text-muted-foreground">{t("dataEntry.values.empty")}</p>}

        {rows.map((row) => (
          <IndicatorValueRow
            key={row.clientKey}
            row={row}
            dataLineList={dataLineList}
            disabled={disabled}
            onUpdate={updateRow}
            onRemove={removeRow}
          />
        ))}

        {!disabled && (
          <Button variant="outline" size="sm" className="mt-1 w-fit" onClick={addRow}>
            <Plus className="mr-1 h-3 w-3" />
            {t("dataEntry.values.addRow")}
          </Button>
        )}
      </div>
    </div>
  );
}
